export const REQUEST_TIMEOUT_MS = 20000;
export const LIST_MATCHED_RD_TIMEOUT_MS = 40000;
export const RETRY_DELAYS_MS = [1000, 2000];
export const MAX_RETRY_ATTEMPTS = 3;

const isAbortError = (error) =>
  error?.name === "AbortError" || error?.code === "ERR_CANCELED";

export const isRetryableFailure = (error) => {
  if (!error || isAbortError(error)) {
    return false;
  }

  const status = error.response?.status;
  if (typeof status === "number" && status >= 400 && status < 600) {
    return true;
  }

  if (error.code === "ECONNABORTED") {
    return true;
  }

  if (String(error.message || "").toLowerCase().includes("timeout")) {
    return true;
  }

  if (!error.response && (error.request || error.code === "ERR_NETWORK")) {
    return true;
  }

  return false;
};

const cancellableDelay = (ms, signal) =>
  new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException("Aborted", "AbortError"));
      return;
    }

    const timeoutId = window.setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, ms);

    const onAbort = () => {
      window.clearTimeout(timeoutId);
      signal?.removeEventListener("abort", onAbort);
      reject(new DOMException("Aborted", "AbortError"));
    };

    signal?.addEventListener("abort", onAbort);
  });

export const executeWithRetries = async ({ execute, signal }) => {
  let lastError;

  for (let attempt = 0; attempt < MAX_RETRY_ATTEMPTS; attempt += 1) {
    if (signal?.aborted) {
      throw new DOMException("Aborted", "AbortError");
    }

    try {
      return await execute({ signal, attempt });
    } catch (error) {
      lastError = error;

      if (signal?.aborted || !isRetryableFailure(error)) {
        throw error;
      }

      const isLastAttempt = attempt >= MAX_RETRY_ATTEMPTS - 1;
      if (isLastAttempt) {
        throw error;
      }

      await cancellableDelay(RETRY_DELAYS_MS[attempt], signal);
    }
  }

  throw lastError;
};
